// The one hook screens use for sound and celebration. Respects the theme's
// sounds flag and page visibility.

import { useCallback, useEffect } from 'react';
import { useTheme } from '../theme/ThemeProvider';
import { playSound, primeAudio, type SoundKind } from './sounds';

export function celebrate(colors: readonly string[], durationMs = 1500): void {
  if (typeof document === 'undefined') return;
  const canvas = document.createElement('canvas');
  canvas.className = 'fsp-confetti';
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  document.body.appendChild(canvas);
  const g = canvas.getContext('2d');
  if (!g) return;
  const parts = Array.from({ length: 150 }, () => ({
    x: canvas.width * (0.3 + Math.random() * 0.4),
    y: canvas.height * 0.4,
    vx: (Math.random() - 0.5) * 14,
    vy: -Math.random() * 14 - 4,
    w: 6 + Math.random() * 6,
    h: 4 + Math.random() * 4,
    rot: Math.random() * Math.PI,
    vr: (Math.random() - 0.5) * 0.3,
    color: colors[Math.floor(Math.random() * colors.length)] ?? '#ffffff',
  }));
  const start = performance.now();
  const frame = (t: number) => {
    const k = (t - start) / durationMs;
    g.clearRect(0, 0, canvas.width, canvas.height);
    for (const p of parts) {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.45;
      p.vx *= 0.99;
      p.rot += p.vr;
      g.save();
      g.translate(p.x, p.y);
      g.rotate(p.rot);
      g.globalAlpha = Math.max(0, 1 - k);
      g.fillStyle = p.color;
      g.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      g.restore();
    }
    if (k < 1) requestAnimationFrame(frame);
    else canvas.remove();
  };
  requestAnimationFrame(frame);
}

export function useDelight() {
  const { theme } = useTheme();
  const enabled = theme.sounds;

  useEffect(() => {
    const prime = () => primeAudio();
    window.addEventListener('pointerdown', prime, { once: true });
    window.addEventListener('keydown', prime, { once: true });
    return () => {
      window.removeEventListener('pointerdown', prime);
      window.removeEventListener('keydown', prime);
    };
  }, []);

  const play = useCallback(
    (kind: SoundKind) => {
      if (!enabled) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      playSound(kind);
    },
    [enabled],
  );

  return { play, celebrate };
}
